import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertOctagon, Clock, Sparkles, ArrowRight, Loader2, FileText, Receipt, CalendarClock, UserX, PackageX, Ship, Banknote, Repeat, MapPin, Truck, ListChecks, Gift, TrendingUp, CheckCircle2, Hourglass, Calculator, PackageCheck, KeyRound, Stamp } from "lucide-react";
import api, { money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";

const ICONS = {
  quote: FileText, invoice: Receipt, overdue: CalendarClock, dormant: UserX, stock: PackageX,
  import: Ship, payable: Banknote, followup: Repeat, visit: MapPin, dispatch: Truck,
  task: ListChecks, repurchase: Gift, rising: TrendingUp, stockout: Hourglass,
  uncosted: Calculator, todeliver: PackageCheck, paycode: KeyRound, uncertified: Stamp,
};

const timeGreet = () => { const h = new Date().getHours(); return h < 12 ? "Buenos días" : h < 19 ? "Buenas tardes" : "Buenas noches"; };

const COMMON_PHRASES = [
  "hoy será un gran día",
  "vamos con toda la energía",
  "a por todas hoy 💪",
  "que sea un día productivo",
  "listos para dar lo mejor",
  "paso a paso se llega lejos",
  "hoy sumamos un día de éxito",
  "con enfoque, todo se logra",
  "hagamos que hoy cuente",
  "la constancia construye grandes cosas",
  "actitud positiva, resultados positivos",
  "hoy es una nueva oportunidad",
  "cada esfuerzo vale la pena",
  "avancemos con paso firme",
  "el equipo se nota en los detalles",
  "hoy dejamos huella",
  "empieza con calma, termina con fuerza",
  "grandes metas, un día a la vez",
  "que las buenas noticias no falten hoy",
  "trabajo bien hecho, orgullo compartido",
  "hoy es tuyo, aprovéchalo",
  "buena energía para todo el día",
  "de a poco, pero sin parar",
  "hoy también se puede",
  "la disciplina lleva al éxito",
  "hagamos historia hoy",
  "con ganas todo sale mejor",
  "un día más para brillar",
  "hoy plantamos, mañana cosechamos",
  "el mejor momento es ahora",
  "sonríe, hoy será productivo",
  "que tu esfuerzo hable por ti",
  "pequeños avances, grandes logros",
  "hoy también damos el 100%",
];

const ROLE_PHRASES = {
  direccion: [
    "aquí está el pulso del negocio",
    "el equipo cuenta con tu liderazgo",
    "grandes decisiones, grandes resultados",
    "hoy movemos la empresa hacia adelante",
    "tu visión marca el rumbo",
    "liderar es servir al equipo",
    "los números cuentan tu historia de hoy",
    "estrategia clara, equipo alineado",
    "hoy construyes el mañana de SELEC",
    "un buen líder inspira con el ejemplo",
    "decisiones firmes, futuro sólido",
    "el crecimiento empieza por arriba",
    "hoy conviertes retos en oportunidades",
    "tu enfoque contagia al equipo",
    "resultados sostenibles, visión de largo plazo",
  ],
  vendedor: [
    "hoy cerramos buenas ventas 💰",
    "cada llamada es una oportunidad",
    "a conquistar clientes hoy",
    "las metas se cumplen vendiendo",
    "un no te acerca al siguiente sí",
    "el cliente feliz siempre regresa",
    "hoy tu cartera crece",
    "vende con pasión, atiende con cariño",
    "cada visita abre una puerta",
    "hoy superas tu meta 🚀",
    "el seguimiento hace la diferencia",
    "conoce al cliente, gana la venta",
    "hoy tu esfuerzo se traduce en comisiones",
    "confianza vende más que precio",
    "una buena atención vale oro",
    "hoy conviertes prospectos en clientes",
    "la venta empieza con una sonrisa",
  ],
  auxiliar: [
    "cuentas claras, negocio sano",
    "hoy dejamos las finanzas al día",
    "cada cobro suma al equipo",
    "orden y control, tu especialidad",
    "los números cuadran gracias a ti",
    "hoy la caja queda impecable",
    "detalle a detalle, todo en orden",
    "tu precisión sostiene la operación",
    "hoy adelantamos la facturación",
    "menos pendientes, más tranquilidad",
    "registro al día, decisiones al día",
    "tu trabajo mantiene todo en marcha",
    "hoy cerramos con todo conciliado",
    "cada comprobante en su lugar",
    "eficiencia y orden, tu sello",
  ],
  mensajero: [
    "buen camino y entregas puntuales 🚚",
    "cada entrega cuenta, éxito en la ruta",
    "maneja seguro y llega a tiempo",
    "hoy dejamos clientes felices",
    "ruta despejada, entregas logradas",
    "tu puntualidad es nuestra imagen",
    "cada paquete llega con tu esfuerzo",
    "hoy conquistas la ruta 🛵",
    "seguridad primero, siempre",
    "una entrega a tiempo vale mucho",
    "tú acercas SELEC a cada cliente",
    "buen viaje y buenas entregas",
    "hoy cumples cada parada",
    "la ruta es tuya, hazla rendir",
    "cliente que recibe a tiempo, cliente contento",
  ],
};

const roleKey = (role = "") => {
  const r = role.toLowerCase();
  if (r.includes("direcc") || r.includes("gerenc")) return "direccion";
  if (r.includes("vended") || r.includes("ventas") || r.includes("comercial")) return "vendedor";
  if (r.includes("auxiliar") || r.includes("financ") || r.includes("contab")) return "auxiliar";
  if (r.includes("mensaj") || r.includes("reparto")) return "mensajero";
  return null;
};

// Hash estable (djb2) para derivar un índice determinístico de una semilla.
const hashSeed = (str = "") => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};

// Frase FIJA por día y por colaborador: misma frase todo el día para el mismo usuario,
// pero distinta entre usuarios y entre días. No se repite al recargar.
const pickPhrase = (role, seedKey) => {
  const key = roleKey(role);
  const pool = [...COMMON_PHRASES, ...(key ? ROLE_PHRASES[key] : [])];
  if (!pool.length) return "";
  return pool[hashSeed(seedKey) % pool.length];
};

// Orden de pestañas solicitado: Hoy → Atención inmediata → Oportunidades.
const TABS = [
  { k: "yellow", value: "hoy", title: "Hoy", icon: Clock, dot: "bg-amber-500", text: "text-amber-500", border: "border-l-amber-500", empty: "Sin tareas ni agenda para hoy. ¡Día despejado! ☕" },
  { k: "red", value: "atencion", title: "Atención inmediata", icon: AlertOctagon, dot: "bg-red-500", text: "text-red-500", border: "border-l-red-500", empty: "Nada urgente por ahora. ¡Bien hecho! 🎉" },
  { k: "green", value: "oportunidades", title: "Oportunidades", icon: Sparkles, dot: "bg-green-500", text: "text-green-500", border: "border-l-green-500", empty: "Sin oportunidades detectadas hoy." },
];

function Row({ item, text }) {
  const navigate = useNavigate();
  const Icon = ICONS[item.icon] || Sparkles;
  return (
    <div className="flex items-center justify-between gap-3 rounded-sm border border-border bg-muted/50 px-3 py-2.5" data-testid={`cc-item-${item.icon}`}>
      <div className="flex items-start gap-3 min-w-0">
        <div className={`h-8 w-8 shrink-0 rounded-sm bg-background flex items-center justify-center ${text}`}><Icon className="h-4 w-4" /></div>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight text-foreground">{item.title} {item.count != null && <span className={`text-xs font-mono ${text}`}>· {item.count}</span>}</p>
          <p className="text-[11px] text-muted-foreground">{item.detail}{item.amount != null ? ` · ${money("GTQ", item.amount)}` : ""}</p>
        </div>
      </div>
      <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs gap-1 shrink-0" onClick={() => navigate(item.link)} data-testid={`cc-act-${item.icon}`}>{item.action} <ArrowRight className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

export function CommandCenter() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/intelligence/command-center").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  // Frase FIJA por día y por colaborador (semilla = fecha + id de usuario).
  // No cambia al recargar; cambia cada día y es distinta entre usuarios.
  const phrase = useMemo(() => {
    const day = new Date(Date.now()-216e5).toISOString().slice(0, 10);
    return pickPhrase(data?.role, `${day}|${user?.id || ""}`);
  }, [data?.role, user?.id]);
  const greet = useMemo(() => timeGreet(), []);

  if (loading) return (
    <section className="space-y-4" data-testid="command-center-loading">
      <div className="h-16 rounded-sm bg-muted/40 animate-pulse" />
      <div className="h-9 w-full max-w-md rounded-sm bg-muted/40 animate-pulse" />
      <div className="grid grid-cols-1 gap-2">
        <div className="h-14 rounded-sm bg-muted/30 animate-pulse" />
        <div className="h-14 rounded-sm bg-muted/30 animate-pulse" />
      </div>
    </section>
  );
  if (!data) return null;
  const firstName = (data.name || "").split(" ")[0] || "";

  return (
    <section className="space-y-4" data-testid="command-center">
      <div className="border-l-[3px] border-primary pl-4">
        <h2 className="font-heading text-2xl font-bold" data-testid="cc-greeting">{greet}, {firstName} 👋 ¡{phrase}!</h2>
        <p className="text-sm text-muted-foreground">Esto es lo que necesita tu atención hoy · <span className="font-semibold text-foreground">{data.role}</span></p>
      </div>

      <Tabs defaultValue="hoy" className="w-full" data-testid="cc-tabs">
        <TabsList className="rounded-sm h-auto flex-wrap gap-1 p-1">
          {TABS.map((t) => {
            const count = (data[t.k] || []).length;
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.value} value={t.value} className="rounded-sm gap-2 text-xs sm:text-sm" data-testid={`cc-tab-${t.value}`}>
                <Icon className={`h-4 w-4 ${t.text}`} />
                <span className="font-bold uppercase tracking-wide">{t.title}</span>
                <span className={`inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[11px] font-mono font-bold ${count > 0 ? `${t.dot} text-white` : "bg-muted-foreground/20 text-muted-foreground"}`}>{count}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {TABS.map((t) => {
          const items = data[t.k] || [];
          return (
            <TabsContent key={t.value} value={t.value} data-testid={`cc-panel-${t.value}`}>
              <Card className={`rounded-sm border shadow-none border-l-4 ${t.border} p-4 space-y-2.5`}>
                {items.length === 0 ? (
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5 py-4 justify-center"><CheckCircle2 className="h-4 w-4 text-green-500" /> {t.empty}</p>
                ) : items.map((item, i) => <Row key={i} item={item} text={t.text} />)}
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {data.summary?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="cc-summary">
          {data.summary.map((x, i) => (
            <Card key={i} className="rounded-sm border shadow-none p-3" data-testid={`cc-sum-${i}`}>
              <p className="text-lg font-heading font-black leading-none truncate">{x.money ? money("GTQ", x.value) : x.value}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-1.5">{x.label}</p>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
